/**
 * PostReactions Component
 * 
 * Displays post reactions with counts and user details
 * Allows authenticated users to add/remove reactions
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Heart, Laugh, Frown, Angry, Sparkles, ThumbsUp, Plus } from 'lucide-react'
import { PostReaction } from '@/lib/services/forumStore.service'
import { usePlayerAuth } from '@/contexts/PlayerAuthContext'

interface PostReactionsProps {
  postId: string
  reactions: PostReaction[]
  onReact: (type: string) => Promise<void>
  onRemoveReaction: () => Promise<void>
}

const REACTION_ICONS: Record<string, { icon: React.ReactNode; label: string; emoji: string }> = {
  LIKE: { icon: <ThumbsUp className="w-4 h-4" />, label: 'Curtir', emoji: '👍' },
  LOVE: { icon: <Heart className="w-4 h-4" />, label: 'Amei', emoji: '❤️' },
  HAHA: { icon: <Laugh className="w-4 h-4" />, label: 'Haha', emoji: '😂' },
  WOW: { icon: <Sparkles className="w-4 h-4" />, label: 'Uau', emoji: '😮' },
  SAD: { icon: <Frown className="w-4 h-4" />, label: 'Triste', emoji: '😢' },
  ANGRY: { icon: <Angry className="w-4 h-4" />, label: 'Raiva', emoji: '😡' },
}

export function PostReactions({ postId, reactions, onReact, onRemoveReaction }: PostReactionsProps) {
  const { player } = usePlayerAuth()
  const [isReactionPickerOpen, setIsReactionPickerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Check if current player has reacted
  const currentPlayerReaction = reactions.find(r => 
    r.users.some(u => u.playerId === player?.id)
  )

  const handleReact = async (type: string) => {
    if (isLoading || !player) return
    
    setIsLoading(true)
    try {
      // If clicking the same reaction, remove it
      if (currentPlayerReaction?.type === type) {
        await onRemoveReaction()
      } else {
        await onReact(type)
      }
      setIsReactionPickerOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Display existing reactions */}
      {reactions.map((reaction) => {
        const reactionConfig = REACTION_ICONS[reaction.type]
        if (!reactionConfig) return null

        const hasCurrentPlayerReacted = reaction.users.some(u => u.playerId === player?.id)

        return (
          <TooltipProvider key={reaction.type}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={hasCurrentPlayerReacted ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleReact(reaction.type)}
                  disabled={isLoading || !player}
                  className={`gap-1.5 transition-all ${
                    hasCurrentPlayerReacted 
                      ? 'bg-primary/20 text-primary border-primary/40 hover:bg-primary/30' 
                      : 'hover:bg-accent/50'
                  }`}
                >
                  <span className="text-base">{reactionConfig.emoji}</span>
                  <span className="font-semibold">{reaction.count}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="text-sm">
                  <p className="font-semibold mb-1">{reactionConfig.label}</p>
                  {reaction.users.length > 0 && (
                    <div className="space-y-0.5 max-h-32 overflow-y-auto">
                      {reaction.users.map((user, idx) => (
                        <p key={idx} className="text-muted-foreground">
                          {user.playerName} (@{user.playerNick})
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      })}

      {/* Add reaction button */}
      {player && (
        <Popover open={isReactionPickerOpen} onOpenChange={setIsReactionPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 hover:bg-accent/50"
              disabled={isLoading}
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Reagir</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" side="top">
            <div className="flex gap-1">
              {Object.entries(REACTION_ICONS).map(([type, config]) => (
                <TooltipProvider key={type}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReact(type)}
                        disabled={isLoading}
                        className="w-12 h-12 p-0 hover:scale-125 transition-transform"
                      >
                        <span className="text-2xl">{config.emoji}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{config.label}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {!player && reactions.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Faça login para reagir
        </p>
      )}
    </div>
  )
}
